import { Injectable } from '@angular/core';

import { mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { AppSettingsResolverActions, AppSettingsModalActions, AppSettingsApiActions } from './app-settings.actions';

import {
  ApplicationSetting,
  ApplicationSettingLink,
  ApplicationSettingsLinkService,
  ApplicationSettingsService,
  MutateApplicationSettings,
} from '@era/settings-sdk';
import { compare } from 'fast-json-patch';
import { concat, EMPTY, Observable, of } from 'rxjs';
import { CategoryApplicationSetting, CategoryType } from './app-settings.state';
import { safeMap } from '../effects.helpers';

@Injectable()
export class AppSettingsEffects {
  constructor(
    private readonly appSettingsLinkService: ApplicationSettingsLinkService,
    private readonly appSettingsService: ApplicationSettingsService,
    private readonly actions$: Actions
  ) {}
  settingsLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppSettingsResolverActions.appSettingsRequested),
      mergeMap(({ categoryType, categoryId, bundleIdentifier }) =>
        safeMap(
          this.getSettingsForCategory(categoryType, categoryId, bundleIdentifier),
          (settings) =>
            AppSettingsApiActions.appSettingsLoaded({
              settings: this.mapToCategoryApplicationSetting(settings, categoryType, categoryId, bundleIdentifier),
            }),
          (error) => AppSettingsApiActions.appSettingsLoadedError({ error })
        )
      )
    );
  });

  settingsUpdatedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppSettingsModalActions.appSettingsUpdated),
      mergeMap((action) => {
        // Get all the information from the action payload.
        const categoryType = action.categoryType;
        const categoryId = action.categoryId;
        const bundleIdentifier = action.bundleIdentifier;

        // Create a setting ID by combining the category ID and the bundle identifier.
        const settingId = categoryId + '-' + bundleIdentifier;

        // Map the new settings to the model which the endpoints require.
        const mappedNewSettings: MutateApplicationSettings[] =
          action.newSettings.length !== 0
            ? action.newSettings.map((x) => {
                const mutateSetting: MutateApplicationSettings = {
                  key: x.key,
                  value: x.value,
                };
                return mutateSetting;
              })
            : [];

        // Map the old settings to the model which the endpoints require.
        const mappedOldSettings: MutateApplicationSettings[] =
          action.oldSettings.length !== 0
            ? action.oldSettings.map((x) => {
                const mutateSetting: MutateApplicationSettings = {
                  key: x.key,
                  value: x.value,
                };
                return mutateSetting;
              })
            : [];

        // Patch the application settings itself.
        const jsonPatch = compare(mappedOldSettings, mappedNewSettings);
        const patchApplicationSettings$ = safeMap(
          this.appSettingsService.patchApplicationSettings(settingId, jsonPatch),
          () =>
            AppSettingsApiActions.appSettingsUpdated({
              categoryType: categoryType,
              categoryId: categoryId,
              bundleIdentifier: bundleIdentifier,
              settings: action.newSettings,
            }),
          (error) => AppSettingsApiActions.appSettingsUpdatedError({ error })
        );

        // Link settings configuration to category when none exist yet.
        const addSettingsToCategory$ =
          mappedOldSettings.length === 0
            ? this.addSettingsToCategory(
                categoryType,
                categoryId,
                this.mapToApplicationSettingLink(bundleIdentifier, settingId)
              )
            : EMPTY;

        // Unlink settings configuration from category when none exist anymore.
        const deleteSettingsFromCategory$ =
          mappedNewSettings.length === 0
            ? this.deleteSettingsFromCategory(categoryType, settingId, categoryId, bundleIdentifier)
            : EMPTY;

        // Combine all the endpoint calls together. It only calls an endpoint when its actually needed.
        return concat(patchApplicationSettings$, addSettingsToCategory$, deleteSettingsFromCategory$);
      })
    );
  });

  private mapToCategoryApplicationSetting(
    settings: ApplicationSetting[],
    categoryType: CategoryType,
    categoryId: string,
    bundleIdentifier: string
  ): CategoryApplicationSetting[] {
    return settings.map((s) => ({
      ...s,
      categoryType: categoryType,
      categoryId: categoryId,
      bundleIdentifier: bundleIdentifier,
    }));
  }

  private mapToApplicationSettingLink(bundleIdentifier: string, applicationSettingId: string): ApplicationSettingLink {
    return {
      bundleIdentifier,
      applicationSettingId,
    };
  }

  private getSettingsForCategory(
    type: CategoryType,
    categoryId: string,
    bundleIdentifier: string
  ): Observable<ApplicationSetting[]> {
    switch (type) {
      case 'Bar':
        return this.appSettingsLinkService.getApplicationSettingsForBar(bundleIdentifier, categoryId);
      case 'Brand':
        return this.appSettingsLinkService.getApplicationSettingsForBrand(bundleIdentifier, categoryId);
      case 'FrontDesk':
        return this.appSettingsLinkService.getApplicationSettingsForFrontdesk(bundleIdentifier, categoryId);
      case 'Housekeeping':
        return this.appSettingsLinkService.getApplicationSettingsForHousekeeping(bundleIdentifier, categoryId);
      case 'Kiosk':
        return this.appSettingsLinkService.getApplicationSettingsForKiosk(bundleIdentifier, categoryId);
      case 'Location':
        return this.appSettingsLinkService.getApplicationSettingsForLocation(bundleIdentifier, categoryId);
      case 'Room':
        return this.appSettingsLinkService.getApplicationSettingsForRoom(bundleIdentifier, categoryId);
      case 'Staff':
        return this.appSettingsLinkService.getApplicationSettingsForStaff(bundleIdentifier, categoryId);
      case 'None':
        return of([]);
    }
  }

  private addSettingsToCategory(
    categoryType: CategoryType,
    categoryId: string,
    applicationSettingLink: ApplicationSettingLink
  ) {
    let apiCall: Observable<ApplicationSetting[]> = undefined;

    switch (categoryType) {
      case 'Bar':
        apiCall = this.appSettingsLinkService.postApplicationSettingsForBar(
          categoryId,
          applicationSettingLink.bundleIdentifier,
          applicationSettingLink
        );
        break;
      case 'Brand':
        apiCall = this.appSettingsLinkService.postApplicationSettingsForBrand(
          categoryId,
          applicationSettingLink.bundleIdentifier,
          applicationSettingLink
        );
        break;
      case 'FrontDesk':
        apiCall = this.appSettingsLinkService.postApplicationSettingsForFrontdesk(
          categoryId,
          applicationSettingLink.bundleIdentifier,
          applicationSettingLink
        );
        break;
      case 'Housekeeping':
        apiCall = this.appSettingsLinkService.postApplicationSettingsForHousekeeping(
          categoryId,
          applicationSettingLink.bundleIdentifier,
          applicationSettingLink
        );
        break;
      case 'Kiosk':
        apiCall = this.appSettingsLinkService.postApplicationSettingsForKiosk(
          categoryId,
          applicationSettingLink.bundleIdentifier,
          applicationSettingLink
        );
        break;
      case 'Location':
        apiCall = this.appSettingsLinkService.postApplicationSettingsForLocation(
          categoryId,
          applicationSettingLink.bundleIdentifier,
          applicationSettingLink
        );
        break;
      case 'Room':
        apiCall = this.appSettingsLinkService.postApplicationSettingsForRoom(
          categoryId,
          applicationSettingLink.bundleIdentifier,
          applicationSettingLink
        );
        break;
      case 'Staff':
        apiCall = this.appSettingsLinkService.postApplicationSettingsForStaff(
          categoryId,
          applicationSettingLink.bundleIdentifier,
          applicationSettingLink
        );
        break;
      case 'None':
        apiCall = of([]);
        break;
    }

    return safeMap(
      apiCall,
      () => AppSettingsApiActions.appSettingsLinkedToCategory({ applicationSettingLink }),
      (error) => AppSettingsApiActions.appSettingsLinkedError({ error })
    );
  }

  private deleteSettingsFromCategory(
    categoryType: CategoryType,
    settingId: string,
    categoryId: string,
    bundleIdentifier: string
  ) {
    let apiCall: Observable<ApplicationSetting[]> = undefined;

    switch (categoryType) {
      case 'Bar':
        apiCall = this.appSettingsLinkService.deleteApplicationSettingsForBar(bundleIdentifier, settingId, categoryId);
        break;
      case 'Brand':
        apiCall = this.appSettingsLinkService.deleteApplicationSettingsForBrand(
          bundleIdentifier,
          settingId,
          categoryId
        );
        break;
      case 'FrontDesk':
        apiCall = this.appSettingsLinkService.deleteApplicationSettingsForFrontdesk(
          bundleIdentifier,
          settingId,
          categoryId
        );
        break;
      case 'Housekeeping':
        apiCall = this.appSettingsLinkService.deleteApplicationSettingsForHousekeeping(
          bundleIdentifier,
          settingId,
          categoryId
        );
        break;
      case 'Kiosk':
        apiCall = this.appSettingsLinkService.deleteApplicationSettingsForKiosk(
          bundleIdentifier,
          settingId,
          categoryId
        );
        break;
      case 'Location':
        apiCall = this.appSettingsLinkService.deleteApplicationSettingsForLocation(
          bundleIdentifier,
          settingId,
          categoryId
        );
        break;
      case 'Room':
        apiCall = this.appSettingsLinkService.deleteApplicationSettingsForRoom(bundleIdentifier, settingId, categoryId);
        break;
      case 'Staff':
        apiCall = this.appSettingsLinkService.deleteApplicationSettingsForStaff(
          bundleIdentifier,
          settingId,
          categoryId
        );
        break;
      case 'None':
        apiCall = of([]);
        break;
    }

    return safeMap(
      apiCall,
      () => AppSettingsApiActions.appSettingsUnlinkedFromCategory({ settingId }),
      (error) => AppSettingsApiActions.appSettingsUnlinkedError({ error })
    );
  }
}
